import { useState } from "react"
import { useDispatch } from "react-redux"

import { ConfigContainer } from "components"
import { Title, Container, Text, Select } from "components/Form"
import { members } from "state_management"
import useT from "utils/hooks/useT"

// Mirrors MemberConfig's language list; the codes the backend accepts on the member payload.
const PLANNERTECH_LANGUAGE_OPTIONS = [
  { id: "cs", name: "Čeština" },
  { id: "da", name: "Dansk" },
  { id: "de", name: "Deutsch" },
  { id: "el", name: "Ελληνικά" },
  { id: "en", name: "English" },
  { id: "es", name: "Español" },
  { id: "fr", name: "Français" },
  { id: "it", name: "Italiano" },
  { id: "ko", name: "한국어" },
  { id: "pl", name: "Polski" },
  { id: "pt", name: "Português" },
]

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
const WORKING_DAYS = new Set(DAYS.slice(0, 5))

// "Add QA member" (SPEC-058 A): the QA Manager's create form. Deliberately a QA-scoped form
// rather than MemberConfig with everything locked: the planning role is FIXED to Member (the
// server refuses anything else from a qa-manager — SPEC-057 L1, the columns never cross), there
// is no password (the new member gets a welcome mail and picks their own — SPEC-057 L2), and the
// planning details (work hours, work week, show-on-plan) take the same defaults MemberConfig
// uses, for the planner to adjust later in Setup › Team if the person also performs tasks.
export default function QaMemberConfig({ onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [initials, setInitials] = useState("")
  const [email, setEmail] = useState("")
  const [plannertechLanguage, setPlannertechLanguage] = useState("")

  const submit = () => {
    const payload = {
      initials,
      email,
      role: "MEMBER",
      showOnPlan: true,
      outlookLink: null,
      workHours: { min: 8 * 3600, max: 16 * 3600 },
      workWeek: Object.fromEntries(DAYS.map((day) => [day, WORKING_DAYS.has(day)])),
    }
    if (plannertechLanguage) {
      payload.plannertechLanguage = plannertechLanguage
    }
    return dispatch(members.create(payload)).then(onClose)
  }

  return (
    <ConfigContainer title={t("limsQaTeamAddMember")} submitHandler={submit} cancelHandler={onClose}>
      <Container>
        <Title>{t("initials")}</Title>
        <Text required value={initials} textTransform={"uppercase"} onChange={setInitials} />
      </Container>
      <Container>
        <Title>{t("emailLabel")}</Title>
        <Text
          required
          type="email"
          value={email}
          textTransform={"lowercase"}
          onValidate={(value) => (value.includes("..") ? t("emailCannotContainDoubleDots") : undefined)}
          onChange={setEmail}
        />
      </Container>
      <Container>
        <Title>{t("plannertechLanguage")}</Title>
        <Select
          isMulti={false}
          collection={PLANNERTECH_LANGUAGE_OPTIONS}
          value={plannertechLanguage}
          onChange={(values) => setPlannertechLanguage(values[0] || "")}
          includeNone
          noneLabel={t("default")}
        />
      </Container>
      <Container>
        <Title>{t("role")}</Title>
        <p className="QaTeam-role-fixed">{t("limsQaTeamRoleFixed")}</p>
        <p className="QaTeam-role-hint">{t("limsQaManagerRoleLocked")}</p>
      </Container>
    </ConfigContainer>
  )
}
